/**
 * Understand Integers and Zero generator (6.NR.2.1).
 *
 * Connects integers (including zero) to real-world situations and their
 * position relative to zero on a number line. The integer is chosen first and
 * the situation is worded around it, so the graded value always matches the
 * scenario.
 *
 * Families:
 *   F1: real-world situation -> signed integer     (number)
 *   F2: which integer describes the situation       (choice)
 *   F3: is the value above/below/equal to zero       (choice)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "understand-integers" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
  /** Whether zero may be used as the value. */
  readonly allowZero: boolean;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F3"], max: 10, allowZero: false },
  standard: { families: ["F1", "F2", "F3"], max: 25, allowZero: true },
  hard: { families: ["F1", "F2", "F3"], max: 60, allowZero: true },
  challenge: { families: ["F2", "F3"], max: 120, allowZero: true },
};

/** A real-world context: positive and negative wordings around a magnitude. */
interface Context {
  readonly noun: string;
  /** Wording for a positive/increase reading of the magnitude. */
  readonly pos: (m: number) => string;
  /** Wording for a negative/decrease reading of the magnitude. */
  readonly neg: (m: number) => string;
}

const CONTEXTS: readonly Context[] = [
  {
    noun: "temperature",
    pos: (m) => `a temperature of ${m} degrees above zero`,
    neg: (m) => `a temperature of ${m} degrees below zero`,
  },
  {
    noun: "elevation",
    pos: (m) => `${m} feet above sea level`,
    neg: (m) => `${m} feet below sea level`,
  },
  {
    noun: "money",
    pos: (m) => `a deposit of $${m}`,
    neg: (m) => `a withdrawal of $${m}`,
  },
  {
    noun: "yardage",
    pos: (m) => `a gain of ${m} yards`,
    neg: (m) => `a loss of ${m} yards`,
  },
  {
    noun: "elevator",
    pos: (m) => `going up ${m} floors`,
    neg: (m) => `going down ${m} floors`,
  },
];

export class UnderstandIntegersGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      const context = rng.pick(CONTEXTS);
      const negative = rng.bool();
      const mag = rng.int(1, cfg.max);
      const useZero = cfg.allowZero && family === "F3" && rng.bool(0.15);
      const intValue = useZero ? 0 : negative ? -mag : mag;
      const phrase = useZero
        ? `${context.noun === "money" ? "no change in the balance" : "no change from zero"}`
        : negative
          ? context.neg(mag)
          : context.pos(mag);

      if (family === "F1") {
        // Situation -> the signed integer (number answer).
        const answer = rat(intValue);
        return {
          id: `${TOPIC}-${ctx.seed}-${family}`,
          generator: TOPIC,
          generatorVersion: VERSION,
          seed: ctx.seed,
          unit: topic.unit,
          topic: TOPIC,
          family,
          difficulty: ctx.difficulty,
          prompt: {
            text: `Write the integer that represents ${phrase}.`,
          },
          answerType: "number",
          answer: { kind: "number", value: answer },
          steps: [
            {
              description:
                "Above zero, gains, and deposits are positive; below zero, losses, and withdrawals are negative.",
              latex: answer.toLatex(),
            },
          ],
          hints: [
            { level: 1, text: "Decide first whether the situation is above or below zero." },
            { level: 2, text: "Gains and increases are positive; losses and decreases are negative." },
          ],
          subskills: topic.subskills,
        };
      }

      if (family === "F2") {
        // Which integer describes the situation (choice among signed ints).
        const correctVal = intValue;
        const wrong1 = -correctVal === 0 ? correctVal + 1 : -correctVal;
        const wrong2 = correctVal + (negative ? 1 : -1);
        const wrong3 = correctVal + (negative ? -2 : 2);
        const raw = [
          { v: correctVal, correct: true },
          { v: wrong1, correct: false },
          { v: wrong2, correct: false },
          { v: wrong3, correct: false },
        ];
        // Dedupe by value, keep the correct one.
        const seen = new Set<number>();
        const uniq: { v: number; correct: boolean }[] = [];
        for (const o of raw) {
          if (seen.has(o.v)) continue;
          seen.add(o.v);
          uniq.push(o);
        }
        if (uniq.length < 2) continue;
        const options = uniq.map((o, i) => ({ id: `c${i}`, v: o.v, correct: o.correct }));
        const shuffled = rng.shuffle(options);
        const choices: Choice[] = shuffled.map((o) => ({
          id: o.id,
          latex: rat(o.v).toLatex(),
        }));
        const correctId = (shuffled.find((o) => o.correct) as { id: string }).id;

        return {
          id: `${TOPIC}-${ctx.seed}-${family}`,
          generator: TOPIC,
          generatorVersion: VERSION,
          seed: ctx.seed,
          unit: topic.unit,
          topic: TOPIC,
          family,
          difficulty: ctx.difficulty,
          prompt: {
            text: `Which integer represents ${phrase}?`,
          },
          answerType: "choice",
          answer: { kind: "choice", value: correctId },
          steps: [
            {
              description:
                "Identify the direction (positive or negative), then attach it to the size of the change.",
              latex: rat(correctVal).toLatex(),
            },
          ],
          hints: [
            { level: 1, text: "Is the situation above zero or below zero?" },
            { level: 2, text: "Below zero, losses, and withdrawals take a negative sign." },
          ],
          subskills: topic.subskills,
          choices,
        };
      }

      // F3: is the value above zero, below zero, or equal to zero?
      const correctId = intValue > 0 ? "above" : intValue < 0 ? "below" : "equal";
      const choices: Choice[] = [
        { id: "above", label: "Above zero", latex: "> 0" },
        { id: "below", label: "Below zero", latex: "< 0" },
        { id: "equal", label: "Equal to zero", latex: "= 0" },
      ];
      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `On a number line, where is the integer for ${phrase}?`,
        },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [
          {
            description:
              "Positive integers sit to the right of zero, negative integers to the left, and zero is the origin.",
            latex: rat(intValue).toLatex(),
          },
        ],
        hints: [
          { level: 1, text: "Positive is right of zero; negative is left of zero." },
          { level: 2, text: "A gain or increase is above zero; a loss or decrease is below zero." },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "choice"
        ? q.answer.value
        : q.answer.kind === "number"
          ? q.answer.value.key()
          : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const understandIntegers = new UnderstandIntegersGenerator();
